import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Calendar,
	ExternalLink,
	FileText,
	Loader2,
	Search,
} from "lucide-react";
import { useState } from "react";
import { useTRPC } from "@/integrations/trpc/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { getStatusColor, getStatusLabel, getEffectiveStatus } from "@/lib/utils";

/**
 * Embeddable solicitations page for iframe integration.
 *
 * This page is self-contained and fetches its own buyer data to avoid
 * SSR hydration issues with parent context providers.
 *
 * For proper CSP frame-ancestors support, use the /api/embed/{slug} endpoint
 * which wraps this page with appropriate security headers.
 */
export const Route = createFileRoute("/public/$slug/embed")({
	component: EmbedSolicitationsPage,
});

function EmbedSolicitationsPage() {
	const { slug } = Route.useParams();
	const trpc = useTRPC();

	// Fetch buyer data directly (self-contained for embed use)
	const { data: buyer, isLoading: buyerLoading, error: buyerError } = useQuery(
		trpc.public.organizations.getBySlug.queryOptions({ slug })
	);
	const [search, setSearch] = useState("");
	const [typeFilter, setTypeFilter] = useState<string>("all");

	// Query public solicitations (only when buyer is loaded)
	const { data: solicitationsData, isLoading: solicitationsLoading } = useQuery({
		...trpc.public.solicitations.list.queryOptions({
			buyerOrgId: buyer?.id ?? "",
			limit: 100,
			offset: 0,
		}),
		enabled: !!buyer?.id,
	});

	// Handle loading state
	if (buyerLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// Handle error/not found state
	if (buyerError || !buyer) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50">
				<div className="text-center">
					<h1 className="text-xl font-bold mb-2">Organization Not Found</h1>
					<p className="text-muted-foreground text-sm">
						The organization you're looking for doesn't exist.
					</p>
				</div>
			</div>
		);
	}

	const isLoading = solicitationsLoading;

	// Filter out drafts and apply search/type filters
	const solicitations = (solicitationsData ?? []).filter((s) => {
		if (s.status === "draft") return false;
		const matchesSearch =
			s.title.toLowerCase().includes(search.toLowerCase()) ||
			(s.description?.toLowerCase().includes(search.toLowerCase()) ?? false);
		const matchesType = typeFilter === "all" || s.type === typeFilter;
		return matchesSearch && matchesType;
	});

	return (
		<div className="min-h-screen bg-gray-50 p-4">
			{/* Compact Header for Embed */}
			<div className="mb-6">
				<div className="flex items-center gap-3 mb-4">
					<div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
						<span className="text-primary-foreground font-bold text-sm">
							{buyer.name.charAt(0).toUpperCase()}
						</span>
					</div>
					<div>
						<h1 className="font-semibold">{buyer.name}</h1>
						<p className="text-xs text-muted-foreground">Procurement Opportunities</p>
					</div>
				</div>

				{/* Search */}
				<div className="relative mb-4">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
					<Input
						placeholder="Search solicitations..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-10"
					/>
				</div>

				{/* Filters */}
				<div className="flex flex-col sm:flex-row gap-2">
					<Select value={typeFilter} onValueChange={setTypeFilter}>
						<SelectTrigger className="w-[150px]">
							<SelectValue placeholder="All Types" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All Types</SelectItem>
							<SelectItem value="RFP">RFP</SelectItem>
							<SelectItem value="RFQ">RFQ</SelectItem>
							<SelectItem value="RFI">RFI</SelectItem>
							<SelectItem value="IFB">IFB</SelectItem>
							<SelectItem value="ITB">ITB</SelectItem>
						</SelectContent>
					</Select>
					<span className="text-sm text-muted-foreground self-center">
						{solicitations.length} opportunities
					</span>
				</div>
			</div>

			{/* Solicitations List */}
			{isLoading ? (
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			) : (
				<div className="space-y-3">
					{solicitations.map((sol) => (
						<Card key={sol.id} className="hover:shadow-md transition-shadow">
							<CardHeader className="pb-2">
								<div className="flex items-start justify-between gap-2">
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 mb-1">
											<Badge variant="outline" className="text-xs">
												{sol.type}
											</Badge>
											<Badge className={`text-xs ${getStatusColor(getEffectiveStatus(sol.status, sol.publish_date, sol.due_date))}`}>
												{getStatusLabel(getEffectiveStatus(sol.status, sol.publish_date, sol.due_date))}
											</Badge>
										</div>
										<CardTitle className="text-base line-clamp-1">
											{sol.title}
										</CardTitle>
										<CardDescription className="text-sm line-clamp-1">
											{sol.description}
										</CardDescription>
									</div>
								</div>
							</CardHeader>
							<CardContent className="pt-2">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-4 text-sm text-muted-foreground">
										<div className="flex items-center gap-1">
											<Calendar className="h-3 w-3" />
											<span>Due: {new Date(sol.due_date).toLocaleDateString()}</span>
										</div>
									</div>
									<Button variant="outline" size="sm" asChild>
										<a
											href={`/public/${slug}/solicitations/${sol.id}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											View
											<ExternalLink className="ml-1 h-3 w-3" />
										</a>
									</Button>
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			)}

			{!isLoading && solicitations.length === 0 && (
				<div className="text-center py-8">
					<FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
					<h3 className="font-medium">No solicitations found</h3>
					<p className="text-sm text-muted-foreground">
						{search || typeFilter !== "all"
							? "Try adjusting your search or filters"
							: "No active opportunities at this time"}
					</p>
				</div>
			)}

			{/* Powered by footer */}
			<div className="mt-6 pt-4 border-t text-center text-xs text-muted-foreground">
				Powered by{" "}
				<a
					href="https://bidscope.ai"
					target="_blank"
					rel="noopener noreferrer"
					className="text-primary hover:underline"
				>
					Bidscope
				</a>
			</div>
		</div>
	);
}
